import { AppHelper } from "../../../appHelper.js";
import { Helper } from "../../util/helper.js";
import {
    BaseColumn,
    CheckBoxColumn,
    ComboBoxColumn,
    DataEntryColumn,
    DropDownColumn,
    DropDownOnDemandColumn,
    MultiColumn,
    Schema,
    SearchBaseColumn,
    TemplateParams,
    TimeTextBox,
    ValidationResult,
    ValidationResultType
} from "./editableGrid.js";

export class EditableGridHelper {
    public static templates = {
        getLookupTemplate: (dropDownColumn: DropDownColumn, params: TemplateParams) => {
            let value = params.row[dropDownColumn.field];
            let lookedUpValue;
            let fallbackTextValue;
            if (dropDownColumn.useDataSource)
                dropDownColumn.choices = dropDownColumn.getChoices();
            if (dropDownColumn.choices != null)
                lookedUpValue = dropDownColumn.choices.find((c) => c.Value == value);
            if (dropDownColumn.fallbackTextField)
                fallbackTextValue = params.row[dropDownColumn.fallbackTextField];
            params.value = (lookedUpValue && lookedUpValue.Text) || fallbackTextValue || value || '';
            return this.helper.getCustumOrDefaultTemplate(params);
        },
        getLookupTemplateOnDemand: (dropDownColumn: DropDownOnDemandColumn, params: TemplateParams) => {
            params.value = params.row[dropDownColumn.lookUpField] || "";
            return this.helper.getCustumOrDefaultTemplate(params);
        },
        getTimeTextBoxTemplate: (timeTextBoxColumn: TimeTextBox, params: TemplateParams) => {
            let value = params.row[timeTextBoxColumn.field];
            params.value = Helper.dates.fromMinutesSinceMidnight(value || 0);
            return this.helper.getCustumOrDefaultTemplate(params);
        },
        getCheckboxTemplate: (checkboxColumn: CheckBoxColumn, params: TemplateParams) => {
            let value = params.row[checkboxColumn.field];
            let htmlString = '<i class="fal fa-square" aria-hidden="true"></i>';
            if (value === true) {
                htmlString = '<i class="fal fa-check" aria-hidden="true"></i>';
            }
            params.value = htmlString;
            return this.helper.getCustumOrDefaultTemplate(params);
        }
    };
    public static groupHeaderColumnTemplate = {
        getTimeTextBoxTemplate: (timeTextBoxColumn: TimeTextBox, data, props) => {
            if (Helper.hasValue(timeTextBoxColumn.groupHeaderColumnTemplate)) {
                let kendoTemplate = kendo.template(timeTextBoxColumn.groupHeaderColumnTemplate);
                let templateData = { ...data[timeTextBoxColumn.field], ...props };
                return kendoTemplate(templateData);
            }
            return '';
        }
    };
    public static groupHeaderTemplate = {
        getIsSearchTemplate: (column: SearchBaseColumn, params: TemplateParams, additionalDataFieldName) => {
            params.value = params.row[additionalDataFieldName]?.Description;
            return this.helper.getCustumOrDefaultTemplate(params);
        },
        getDropDownTemplate: (column: DropDownColumn, params: TemplateParams) => {
            let value = params.row[column.field];
            let lookedUpValue = column.choices.find(x => x.Value == params.value);
            let fallbackTextValue;
            if (column.fallbackTextField)
                fallbackTextValue = params.row[column.fallbackTextField];
            params.value = (lookedUpValue && lookedUpValue.Text) || fallbackTextValue || value || '';
            return this.helper.getCustumOrDefaultTemplate(params);
        },
        getDropDownOnDemandTemplate: (column: DropDownOnDemandColumn, params: TemplateParams) => {
            params.value = params.row != null ? params.row[column.lookUpField] : "";
            return this.helper.getCustumOrDefaultTemplate(params);
        }
    }
    public static editors = {
        getMultiLineEditor: (column: DataEntryColumn, schema: Schema, container, options) => {
            let required = EditableGridHelper.helper.getRequiredAttributes(column, schema, options);
            $(`<textarea name='${options.field}' ${required} />`)
                .appendTo(container)
                .kendoTextArea({
                    value: options.model[options.field],
                    change: (e) => {
                        let value = e.sender.value();
                        options.model.set(options.field, value);
                    },
                    rows: column.multiLines ?? 5
                });
        },
        getTimeTextBoxEditor: (container, options) => {
            $('<input />')
                .appendTo(container)
                .kendoMaskedTextBox({
                    value: Helper.dates.fromMinutesSinceMidnight(options.model[options.field] || 0),
                    mask: "00:00",
                    change: (e) => {
                        let value = e.sender.value();
                        if (value === "")
                            value = "0";
                        let displayValue = Helper.dates.prepareTime(value);
                        options.model.set(options.field, Helper.dates.toMinutesSinceMidnight(displayValue));
                    }
                });
        },
        getComboBoxEditor: async (comboBoxColumn: ComboBoxColumn, schema: Schema, container, options) => {
            if (comboBoxColumn.useDataSource)
                comboBoxColumn.choices = comboBoxColumn.getChoices();
            let required = EditableGridHelper.helper.getRequiredAttributes(comboBoxColumn, schema, options);
            let comboBox = $(`<input name='${options.field}' ${required} />`)
                .appendTo(container)
                .kendoComboBox({
                    valuePrimitive: true, //N.B. this is needed to have correct behavior when the initial value can be null
                    autoBind: false,
                    dataTextField: "Text",
                    dataValueField: "Value",
                    dataSource: {
                        data: comboBoxColumn.choices
                    },
                    change: function (e) {
                        if (e.sender.dataItem() == null) {
                            options.model.set("result", null);
                        } else {
                            options.model.set("result", e.sender.dataItem().Value);
                        }
                    },
                    autoWidth: true,
                    template: comboBoxColumn.itemTemplate
                }).data("kendoComboBox");

            if (Helper.hasValue(comboBoxColumn.listWidth))
                comboBox.list.width(comboBoxColumn.listWidth);
        },
        getDropDownEditor: async (dropDownColumn: DropDownColumn, schema: Schema, container, options) => {
            let choices = dropDownColumn.choices;
            if (dropDownColumn.useDataSource)
                choices = dropDownColumn.getChoices();
            if (dropDownColumn.refreshChoices)
                choices = dropDownColumn.refreshChoices(options.model);
            let required = EditableGridHelper.helper.getRequiredAttributes(dropDownColumn, schema, options);
            let dropDown = $(`<input name='${options.field}' ${required} />`)
                .appendTo(container)
                .kendoDropDownList({
                    valuePrimitive: true, //N.B. this is needed to have correct behavior when the initial value can be null
                    autoBind: false,
                    optionLabel: dropDownColumn.optionLabel,
                    dataTextField: "Text",
                    dataValueField: "Value",
                    dataSource: {
                        data: choices
                    },
                    filter: (dropDownColumn.filterable && Helper.hasValue(dropDownColumn.filter)) ? dropDownColumn.filter : "none",
                    change: function (e) {
                        if (e.sender.dataItem() == null) {
                            options.model.set("result", null);
                        } else {
                            options.model.set("result", e.sender.dataItem().Value);
                        }
                    },
                    autoWidth: true,
                    template: dropDownColumn.itemTemplate,
                    valueTemplate: dropDownColumn.valueTemplate,

                }).data("kendoDropDownList");

            if (Helper.hasValue(dropDownColumn.listWidth))
                dropDown.list.width(dropDownColumn.listWidth);
        },
        getDropDownOnDemandEditor: async (dropDownColumn: DropDownOnDemandColumn, schema: Schema, container, options) => {
            async function setDropDownList() {
                let choices = await dropDownColumn.getChoices(options.model);
                dropDown.dataSource.data(choices);
                let value = options.model[dropDownColumn.field];
                dropDown.value(value);
            };
            let required = EditableGridHelper.helper.getRequiredAttributes(dropDownColumn, schema, options);
            let dropDown = $(`<input name='${options.field}' ${required} />`)
                .appendTo(container)
                .kendoDropDownList({
                    valuePrimitive: true, //N.B. this is needed to have correct behavior when the initial value can be null
                    autoBind: false,
                    optionLabel: dropDownColumn.optionLabel,
                    dataTextField: "Text",
                    dataValueField: "Value",
                    dataSource: {
                        data: null
                    },
                    change: function (e) {
                        if (e.sender.dataItem() == null) {
                            options.model.set("result", null);
                        } else {
                            let value = e.sender.dataItem().Value;
                            let text = e.sender.dataItem().Text;
                            if (Helper.isNullOrEmpty(value))
                                value = null;
                            options.model.set(dropDownColumn.field, value);
                            options.model.set(dropDownColumn.lookUpField, text);
                            options.model.set("result", value);
                        }
                    },
                    autoWidth: true,
                    template: dropDownColumn.itemTemplate,
                    valueTemplate: dropDownColumn.valueTemplate,
                    open: (e) => {
                        if (dropDownColumn.evalUrl != dropDownColumn.getUrl(options.model)) {
                            setDropDownList();
                        }
                    }
                }).data("kendoDropDownList");

            setDropDownList();

            if (Helper.hasValue(dropDownColumn.listWidth))
                dropDown.list.width(dropDownColumn.listWidth);
        }
    };
    public static helper = {
        getCustumOrDefaultTemplate: (params: TemplateParams) => {
            if (!Helper.hasValue(params.kendoTemplate)) {
                let defaultTemplate = this.helper.getDefaultTemplate(params.value);
                if (!Helper.hasValue(defaultTemplate) || defaultTemplate == "undefined")
                    return "";
                return defaultTemplate;
            }
            else {
                return this.helper.getTemplateString(params) ?? "";
            }
        },
        getDefaultTemplate: (value) => {
            let kendoTemplate = kendo.template("#=value#");
            let templateData = { value: value };
            return kendoTemplate(templateData);
        },
        getTemplateString: (params: TemplateParams) => {
            if (!params.field) {
                return `${params.value}`
            }

            try {
                const template = kendo.template(params.kendoTemplate);
                const templateData: any = { ...params.row, row: params.row, field: params.field, value: params.value, ...params.props, ...params.functions }
                var result = template(templateData);
            }
            catch (e) {
                return '<div style = "color:red">Error im template:' + e.message + '</div>';
            }
            return result
        },
        getTemplate: (column: kendo.ui.GridColumn, row) => {
            let template = column.template;
            let format = column.format;
            let value = row[column.field];
            if (typeof template == "function")
                value = template(row);
            else if (template)
                value = template;
            else if (format)
                value = kendo.format(format, row[column.field]);

            return value;
        },
        getRequiredAttributes: (column: BaseColumn, schema: Schema, options: any): string => {
            if (!column.required)
                return;
            let field = schema.fields[options.field];
            return column.required(options.model) ? `required='required' data-required-msg='${field?.Validation?.required?.message}'` : null;
        },
        checkRequiredFields: (columns: BaseColumn[], dataItem): ValidationResult => {
            let message = "";
            let validationType: ValidationResultType = ValidationResultType.Info;
            let translation: string = Helper.getTranslation("Standard.Pflichtfeld");

            for (let column of columns) {
                if (column instanceof DataEntryColumn) {
                    if (column.required && column.required(dataItem) && (!Helper.hasValue(dataItem[column.field]) || dataItem[column.field] === "")) {
                        message += translation.replace("\"{0}\"", column.title) + "\r\n";
                        validationType = ValidationResultType.Error;
                    }
                }
                if (column instanceof MultiColumn) {
                    let resultMultiColumn = this.helper.checkRequiredFields(column.columns, dataItem);
                    message += resultMultiColumn.validationMessage;
                    if (resultMultiColumn.validationResultType > validationType)
                        validationType = resultMultiColumn.validationResultType;
                }
            }
            let result: ValidationResult = { validationMessage: message, validationResultType: validationType };
            return result;
        },
        getRow: (data) => {
            let item = data.items.length > 0 ? data.items[0] : null;
            if (Helper.hasValue(item.items)) {
                return this.helper.getRow(item);
            } else {
                return item;
            }
        },
        evalRow: (value: string, row) => {
            let regex = "row.[a-zA-Z0-9]*";
            let evalValue = value;
            if (!Helper.hasValue(evalValue))
                return;
            while (evalValue.match(regex) !== null) {
                let match = evalValue.match(regex);
                let value = new AppHelper().evalAction(match[0], { row: row });
                evalValue = evalValue.replace(match[0], value);
            }
            return evalValue;
        }
    }
}